import { auth } from 'data/firestore/firebase';
import { ACTIONS_BASE_URL } from '../../../constants';
import {
    Invoice,
    InvoiceAcceptance,
    InvoiceBuilderErrorBody,
    InvoiceEnvelope,
    InvoiceSigningSessionEnvelope,
} from '../types';
import { MICRO_APP_UNAVAILABLE } from '../constants';
import { invoicePath, isSignatureRequested, signingSessionPath } from '../helpers';

export class InvoiceBuilderApiError extends Error {
    status: number;

    code: string;

    details?: InvoiceBuilderErrorBody['details'];

    constructor(
        status: number,
        code: string,
        message: string,
        details?: InvoiceBuilderErrorBody['details'],
    ) {
        super(message);
        this.name = 'InvoiceBuilderApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

export const isAppUnavailable = (error: unknown) => (
    error instanceof InvoiceBuilderApiError
    && error.status === 404
    && error.code === MICRO_APP_UNAVAILABLE
);

const createHeaders = async () => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken();
        headers.Authorization = `Bearer ${idToken}`;
    }
    return headers;
};

const parseError = async (response: Response): Promise<InvoiceBuilderApiError> => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const body = await response.json() as InvoiceBuilderErrorBody;
        return new InvoiceBuilderApiError(
            response.status,
            body.code || 'UNKNOWN',
            body.message || 'Request failed',
            body.details,
        );
    }
    const text = await response.text();
    const code = response.status === 404 ? MICRO_APP_UNAVAILABLE : 'UNKNOWN';
    return new InvoiceBuilderApiError(response.status, code, text || 'Request failed');
};

export const invoiceBuilderRequest = async <T>(
    method: string,
    pathname: string,
    body?: unknown,
): Promise<T> => {
    const url = new URL(pathname, ACTIONS_BASE_URL).toString();
    const response = await fetch(url, {
        method,
        headers: await createHeaders(),
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
        throw await parseError(response);
    }
    if (response.status === 204) {
        return undefined as T;
    }
    return response.json() as Promise<T>;
};

export const fetchInvoice = (organizationId: string, invoiceId: string) => (
    invoiceBuilderRequest<InvoiceEnvelope>('GET', invoicePath(organizationId, invoiceId))
);

export const fetchSigningSession = (organizationId: string, invoiceId: string) => (
    invoiceBuilderRequest<InvoiceSigningSessionEnvelope>(
        'GET',
        signingSessionPath(organizationId, invoiceId),
    )
);

export const createSigningSession = (organizationId: string, invoiceId: string) => (
    invoiceBuilderRequest<InvoiceSigningSessionEnvelope>(
        'POST',
        signingSessionPath(organizationId, invoiceId),
    )
);

export const revokeSigningSession = (organizationId: string, invoiceId: string) => (
    invoiceBuilderRequest<InvoiceSigningSessionEnvelope>(
        'DELETE',
        signingSessionPath(organizationId, invoiceId),
    )
);

// The read endpoint does not return the URL, and it must not be persisted, so it is
// kept in memory for the lifetime of the page.
const createdSigningLinks = new Map<string, { url: string; expiresAt: string | null }>();

const signingLinkKey = (organizationId: string, invoiceId: string) => (
    `${organizationId}/${invoiceId}`
);

export const rememberSigningLink = (
    organizationId: string,
    invoiceId: string,
    link: { url: string; expiresAt: string | null },
) => {
    createdSigningLinks.set(signingLinkKey(organizationId, invoiceId), link);
};

export const recallSigningLink = (organizationId: string, invoiceId: string) => (
    createdSigningLinks.get(signingLinkKey(organizationId, invoiceId))
);

export const forgetSigningLink = (organizationId: string, invoiceId: string) => {
    createdSigningLinks.delete(signingLinkKey(organizationId, invoiceId));
};

const missingSigningSession = (error: unknown) => (
    error instanceof InvoiceBuilderApiError
    && (error.status === 404 || error.status === 410)
);

export const loadInvoiceAcceptance = async (
    organizationId: string,
    invoice: Invoice,
): Promise<InvoiceAcceptance | null> => {
    if (!isSignatureRequested(invoice.document.design)) return null;
    try {
        const session = await fetchSigningSession(organizationId, invoice.id);
        return session.data.acceptance;
    } catch (error) {
        if (missingSigningSession(error)) return null;
        throw error;
    }
};

export const fetchInvoiceForOutput = async (organizationId: string, invoiceId: string) => {
    const { data: invoice } = await fetchInvoice(organizationId, invoiceId);
    const acceptance = await loadInvoiceAcceptance(organizationId, invoice);
    return { invoice, acceptance };
};
